/* SEARCH-TAG: template */
import React, { useState, useLayoutEffect, useContext } from 'react';
import { graphql } from 'gatsby';
import { shape, arrayOf } from 'prop-types';
import styled from 'styled-components';
import { spacing } from 'TMDesignSystem/spacing';
import { breakpoints } from 'TMConstants';
import HeadTags from 'TMComponents/HeadTags';
import Main from 'TMComponents/Layout/Main';
import Canonical from 'TMComponents/Canonical';
import { BreadcrumbsContext } from 'TMComponents/Breadcrumbs';
import { BannerContext } from 'TMComponents/Banner';
import { LayoutContext } from 'TMComponents/Layout';
import AuthorBio from 'TMComponents/AuthorBio';
import { Button } from 'TMComponents/Button';

import BlogCard from 'TMComponents/BlogCard';

const PageContainer = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 ${spacing.medium};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        padding: 0 ${spacing.large};
    }
`;

const ArticlesSection = styled.div`
    margin: ${spacing.xxLarge} 0;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${spacing.mediumLarge};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: ${breakpoints.tabletDesktop}px) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const CenteredSectionHeading = styled.h2`
    text-align: center;
    font-size: 1.75rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: 0 0 ${spacing.large} 0;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        font-size: 2rem;
    }

    span {
        color: ${({ theme }) => theme.colorSystem.secondary[3]};
    }
`;

const LoadMoreWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin: ${spacing.xxLarge} 0;
`;

const AuthorPage = ({ data }) => {
    const [pageNo, setPageNo] = useState(1);
    const { setIsNavHidden } = useContext(LayoutContext);
    const { setShowBanner } = useContext(BannerContext);
    const { setCrumbs } = useContext(BreadcrumbsContext);

    const { author, articles } = data;

    useLayoutEffect(() => {
        setIsNavHidden(false);
        setShowBanner(true);
        setCrumbs({
            show: true,
            crumbList: [
                {
                    title: 'Meet the experts',
                    link: '/blog/meet-the-experts/'
                },
                {
                    title: author.name,
                    link: `/blog/meet-the-experts/${author.slug}/`
                }
            ]
        });
    }, [
        setIsNavHidden,
        setShowBanner,
        setCrumbs,
        author.name,
        author.slug
    ]);

    return (
        <Main
            isBannerAllowed
            isCrumbsAllowed
            reducedCrumbSpacing
            whiteBackground>
            <HeadTags
                tags={[
                    {
                        tagName: 'title',
                        content: `${author.name} - ${author.jobTitle} | thinkmoney`
                    },
                    {
                        tagName: 'meta',
                        attributes: {
                            name: 'description',
                            content: `Read articles by ${author.name}, ${author.jobTitle} at thinkmoney. Expert insights on financial planning and budgeting.`
                        }
                    }
                ]}
            />
            <Canonical href={`blog/meet-the-experts/${author.slug}`} />
            <PageContainer>
                <AuthorBio author={author} />

                {articles.nodes.length > 0 && (
                    <ArticlesSection>
                        <CenteredSectionHeading>
                            Blogs by {author.name}
                            <span>.</span>
                        </CenteredSectionHeading>
                        <CardsGrid>
                            {articles.nodes
                                .slice(0, 6 * pageNo)
                                .map((article) => (
                                    <BlogCard
                                        key={article.id}
                                        title={article.title}
                                        slug={article.slug}
                                        image={
                                            article.featuredImage
                                                ?.gatsbyImageData
                                        }
                                        excerpt={article.summary}
                                        tags={
                                            article.categories?.map(
                                                (cat) => cat.name
                                            ) || []
                                        }
                                        variant="default"
                                    />
                                ))}
                        </CardsGrid>
                        {articles.nodes.length > 6 * pageNo && (
                            <LoadMoreWrapper>
                                <Button
                                    onClick={() => setPageNo(pageNo + 1)}>
                                    Load more articles
                                </Button>
                            </LoadMoreWrapper>
                        )}
                    </ArticlesSection>
                )}
            </PageContainer>
        </Main>
    );
};

export const pageQuery = graphql`
    query ($slug: String!) {
        author: datoCmsAuthor(slug: { eq: $slug }) {
            id
            name
            slug
            jobTitle
            email
            longBio
            photo {
                gatsbyImageData(
                    width: 120
                    height: 120
                    layout: CONSTRAINED
                    placeholder: NONE
                )
            }
        }

        articles: allDatoCmsBlogArticle(
            filter: { author: { slug: { eq: $slug } } }
            sort: { date: DESC }
        ) {
            nodes {
                id
                title
                slug
                summary
                date(formatString: "MMMM DD, YYYY")
                author {
                    name
                    slug
                }
                categories {
                    name
                }
                featuredImage {
                    gatsbyImageData(
                        width: 400
                        layout: CONSTRAINED
                        placeholder: NONE
                    )
                }
            }
        }
    }
`;

AuthorPage.propTypes = {
    data: shape({
        author: shape({}),
        articles: shape({
            nodes: arrayOf(shape({}))
        })
    }).isRequired
};

export default AuthorPage;
